import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Hash, Volume2, Megaphone, Radio, Send, Plus, Users, Smile, ChevronDown,
  Bell, Pin, Search, Gift, Sticker, Paperclip, Zap, Crown, Shield,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CHANNEL_ICON = { text: Hash, voice: Volume2, announcement: Megaphone, live: Radio };
const ROLE_COLOR = {
  owner: "hsl(var(--nx-amber))",
  admin: "hsl(var(--nx-blue))",
  moderator: "hsl(var(--nx-cyan))",
  member: "hsl(var(--muted-foreground))",
};
const ROLE_ICON = { owner: Crown, admin: Shield, moderator: Shield };

function MessageItem({ msg, onReact }) {
  const time = new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="group flex gap-3 px-4 py-1.5 hover:bg-white/[0.03] rounded-md nx-fade-up" data-testid={`msg-${msg.id}`}>
      <Avatar className="w-9 h-9 mt-0.5 ring-2 ring-transparent group-hover:ring-[hsl(var(--nx-border))]">
        <AvatarImage src={msg.author?.avatar_url} />
        <AvatarFallback className="text-xs font-semibold bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))] text-white">
          {msg.author?.username?.[0]?.toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-sm text-foreground">{msg.author?.display_name || msg.author?.username}</span>
          <span className="text-[10px] text-muted-foreground font-mono">{time}</span>
        </div>
        <div className="text-sm whitespace-pre-wrap break-words text-foreground/90">{msg.content}</div>
        {Object.keys(msg.reactions || {}).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {Object.entries(msg.reactions).map(([emoji, users]) => (
              <button key={emoji} onClick={() => onReact(msg.id, emoji)}
                className="text-xs px-2 py-0.5 rounded-full bg-[hsl(var(--nx-panel-3))] hover:bg-[hsl(var(--nx-blue))]/20 border border-[hsl(var(--nx-border))] hover:border-[hsl(var(--nx-blue))] transition-colors"
                data-testid={`react-${msg.id}-${emoji}`}>
                {emoji} <span className="font-mono ml-1 text-muted-foreground">{users.length}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <button onClick={() => onReact(msg.id, "🔥")}
        className="opacity-0 group-hover:opacity-100 self-start text-muted-foreground hover:text-[hsl(var(--nx-blue))] transition-opacity"
        data-testid={`react-btn-${msg.id}`} title="Reagir com 🔥">
        <Smile className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function ServerPage() {
  const { serverId, channelId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [server, setServer] = useState(null);
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [openNewChannel, setOpenNewChannel] = useState(false);
  const [newChannel, setNewChannel] = useState({ name: "", type: "text" });
  const wsRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!serverId) return;
    setMessages([]);
    Promise.all([
      api.get(`/servers/${serverId}`).then((r) => setServer(r.data)),
      api.get(`/servers/${serverId}/channels`).then((r) => {
        setCategories(r.data.categories);
        setChannels(r.data.channels);
      }),
      api.get(`/servers/${serverId}/members`).then((r) => setMembers(r.data)),
    ]).catch((e) => { if (e.response?.status === 404) toast.error("Comunidade não encontrada"); });
  }, [serverId]);

  const currentChannel = useMemo(() => {
    if (!channels.length) return null;
    if (channelId) return channels.find((c) => c.id === channelId) || channels.find((c) => c.type === "text");
    return channels.find((c) => c.type === "text") || channels[0];
  }, [channels, channelId]);

  useEffect(() => {
    if (!currentChannel) return;
    if (currentChannel.type !== "text" && currentChannel.type !== "announcement") return;
    api.get(`/channels/${currentChannel.id}/messages`).then((r) => setMessages(r.data)).catch(() => {});

    const wsUrl = API_BASE.replace(/^http/, "ws") + `/ws/channels/${currentChannel.id}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "message.new") {
          setMessages((m) => (m.some((x) => x.id === data.data.id) ? m : [...m, data.data]));
        } else if (data.type === "message.react") {
          setMessages((m) => m.map((x) => (x.id === data.data.id ? { ...x, reactions: data.data.reactions } : x)));
        } else if (data.type === "message.delete") {
          setMessages((m) => m.filter((x) => x.id !== data.data.id));
        }
      } catch {}
    };
    ws.onclose = () => { if (wsRef.current === ws) wsRef.current = null; };
    return () => { try { ws.close(); } catch {} };
  }, [currentChannel]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!content.trim() || !currentChannel) return;
    try {
      await api.post(`/channels/${currentChannel.id}/messages`, { content: content.trim() });
      setContent("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const react = async (messageId, emoji) => {
    try { await api.post(`/messages/${messageId}/react`, { emoji }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const createChannel = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/servers/${serverId}/channels`, newChannel);
      setChannels((c) => [...c, data]);
      setOpenNewChannel(false);
      setNewChannel({ name: "", type: "text" });
      toast.success(`Canal #${data.name} criado`);
      nav(`/app/servidor/${serverId}/canal/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!server) {
    return <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm font-mono">carregando comunidade…</div>;
  }
  const ChIcon = currentChannel ? CHANNEL_ICON[currentChannel.type] || Hash : Hash;

  // group members by role
  const membersByRole = members.reduce((acc, m) => {
    (acc[m.role] = acc[m.role] || []).push(m);
    return acc;
  }, {});
  const roleOrder = ["owner", "admin", "moderator", "member"];

  return (
    <>
      {/* CHANNEL SIDEBAR */}
      <aside className="w-64 shrink-0 bg-[hsl(var(--nx-panel))] border-r border-[hsl(var(--nx-border))] flex flex-col overflow-hidden" data-testid="channel-sidebar">
        <div className="p-4 border-b border-[hsl(var(--nx-border))] relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--nx-blue))/0.12] to-transparent" />
          <div className="relative">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))] grid place-items-center font-display font-bold text-white">
                {server.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-sm truncate flex items-center gap-1">
                  {server.name}
                  <span className="text-[hsl(var(--nx-cyan))]" title="verificada">✓</span>
                </div>
                <div className="text-[11px] text-muted-foreground truncate">{server.member_count} membros</div>
              </div>
            </div>
            {/* Community level bar */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                <span>Nível 3</span>
                <span className="font-mono">350 boosts</span>
              </div>
              <div className="h-1.5 rounded-full bg-[hsl(var(--nx-panel-3))] overflow-hidden">
                <div className="h-full w-[65%] bg-gradient-to-r from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))]" />
              </div>
            </div>
          </div>
        </div>

        {/* Community mgmt links */}
        <div className="p-2 space-y-0.5 border-b border-[hsl(var(--nx-border))]">
          {[
            { l: "Início", i: Radio, to: `/app/servidor/${serverId}` },
            { l: "Convites", i: Gift, to: `/app/servidor/${serverId}` },
            { l: "Membros", i: Users, to: `/app/servidor/${serverId}` },
          ].map((it) => (
            <Link key={it.l} to={it.to} className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--nx-panel-3))]/50">
              <it.i className="w-4 h-4" /> {it.l}
            </Link>
          ))}
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-3">
            {categories.map((cat) => (
              <div key={cat.id}>
                <div className="flex items-center justify-between px-2 mb-1">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                    <ChevronDown className="w-3 h-3" /> {cat.name}
                  </div>
                  <button onClick={() => setOpenNewChannel(true)}
                    className="opacity-60 hover:opacity-100 hover:text-[hsl(var(--nx-blue))]"
                    data-testid={`new-channel-btn-${cat.id}`} title="Novo canal">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="space-y-0.5">
                  {channels.filter((c) => c.category_id === cat.id).map((c) => {
                    const Icon = CHANNEL_ICON[c.type] || Hash;
                    const active = currentChannel?.id === c.id;
                    return (
                      <button key={c.id}
                        onClick={() => nav(`/app/servidor/${serverId}/canal/${c.id}`)}
                        data-testid={`channel-${c.id}`}
                        className={`w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2 text-sm transition-colors
                        ${active ? "bg-[hsl(var(--nx-blue))]/15 text-foreground" : "text-muted-foreground hover:bg-[hsl(var(--nx-panel-3))]/60 hover:text-foreground"}`}>
                        <Icon className={`w-4 h-4 shrink-0 ${active ? "text-[hsl(var(--nx-blue))]" : ""}`} />
                        <span className="truncate">{c.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </aside>

      {/* CHAT AREA */}
      <section className="flex-1 min-w-0 flex flex-col overflow-hidden bg-[hsl(var(--background))]">
        <header className="h-14 shrink-0 border-b border-[hsl(var(--nx-border))] px-4 flex items-center gap-3 bg-[hsl(var(--nx-panel))]/60">
          <ChIcon className="w-5 h-5 text-muted-foreground" />
          <div className="font-semibold">{currentChannel?.name || "…"}</div>
          {currentChannel?.topic && (
            <>
              <div className="w-px h-5 bg-[hsl(var(--nx-border))]" />
              <div className="text-sm text-muted-foreground truncate">{currentChannel.topic}</div>
            </>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <button className="w-8 h-8 rounded-md hover:bg-[hsl(var(--nx-panel-3))] grid place-items-center" title="Fixados"><Pin className="w-4 h-4 text-muted-foreground" /></button>
            <button className="w-8 h-8 rounded-md hover:bg-[hsl(var(--nx-panel-3))] grid place-items-center" title="Notificações"><Bell className="w-4 h-4 text-muted-foreground" /></button>
            <button className="w-8 h-8 rounded-md hover:bg-[hsl(var(--nx-panel-3))] grid place-items-center" title="Buscar"><Search className="w-4 h-4 text-muted-foreground" /></button>
          </div>
        </header>

        {currentChannel && (currentChannel.type === "text" || currentChannel.type === "announcement") ? (
          <>
            <div ref={scrollerRef} className="flex-1 overflow-y-auto scrollbar-thin py-3">
              {messages.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--nx-panel-2))] grid place-items-center mx-auto mb-4">
                    <Hash className="w-8 h-8 opacity-40" />
                  </div>
                  <p className="font-display font-semibold text-foreground mb-1">Bem-vindo a #{currentChannel.name}</p>
                  <p className="text-sm">Diga oi 👋 para começar a conversa.</p>
                </div>
              )}
              {messages.map((m) => <MessageItem key={m.id} msg={m} onReact={react} />)}
            </div>
            <form onSubmit={sendMessage} className="p-3 border-t border-[hsl(var(--nx-border))]">
              <div className="flex gap-2 items-center nx-panel-2 rounded-2xl px-3 pl-4">
                <button type="button" className="text-muted-foreground hover:text-[hsl(var(--nx-blue))]"><Paperclip className="w-4 h-4" /></button>
                <Input value={content} onChange={(e) => setContent(e.target.value)}
                  placeholder={`Mensagem para #${currentChannel.name}`}
                  className="border-0 bg-transparent focus-visible:ring-0 h-11 px-0"
                  maxLength={4000} data-testid="chat-input" />
                <button type="button" className="text-muted-foreground hover:text-[hsl(var(--nx-blue))]"><Sticker className="w-4 h-4" /></button>
                <button type="button" className="text-muted-foreground hover:text-[hsl(var(--nx-blue))]"><Smile className="w-4 h-4" /></button>
                <Button type="submit" size="icon" disabled={!content.trim()}
                  className="rounded-xl nx-btn-blue border-0 shrink-0" data-testid="chat-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </>
        ) : currentChannel?.type === "live" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3 p-8">
            <Radio className="w-12 h-12 text-[hsl(var(--nx-blue))]" />
            <p className="text-sm">Este canal exibirá as transmissões da comunidade.</p>
            <Link to="/app/explorar" className="text-[hsl(var(--nx-cyan))] hover:underline text-sm" data-testid="live-channel-explore-link">Ver lives agora</Link>
          </div>
        ) : currentChannel?.type === "voice" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3 p-8">
            <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--nx-cyan))]/15 grid place-items-center">
              <Volume2 className="w-8 h-8 text-[hsl(var(--nx-cyan))]" />
            </div>
            <p className="text-sm">Sala de voz — funcionalidade de áudio será liberada em breve.</p>
          </div>
        ) : null}
      </section>

      {/* MEMBERS */}
      <aside className="w-64 shrink-0 border-l border-[hsl(var(--nx-border))] bg-[hsl(var(--nx-panel))] overflow-y-auto scrollbar-thin hidden lg:block" data-testid="members-panel">
        <div className="p-4 border-b border-[hsl(var(--nx-border))] flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
          <Users className="w-3.5 h-3.5" /> Membros — {members.length}
        </div>
        <div className="p-3 space-y-4">
          {roleOrder.map((role) => {
            const list = membersByRole[role] || [];
            if (!list.length) return null;
            return (
              <div key={role}>
                <div className="px-1 mb-2 text-[10px] uppercase tracking-widest font-semibold" style={{ color: ROLE_COLOR[role] || "hsl(var(--muted-foreground))" }}>
                  {role === "owner" ? "DONO" : role === "admin" ? "ADMIN" : role === "moderator" ? "MODERADOR" : "MEMBRO"} — {list.length}
                </div>
                <div className="space-y-0.5">
                  {list.map((m) => {
                    const RoleI = ROLE_ICON[m.role];
                    return (
                      <Link key={m.id} to={`/app/criador/${m.username}`}
                        data-testid={`member-${m.id}`}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[hsl(var(--nx-panel-3))]/60 transition-colors">
                        <div className="relative">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={m.avatar_url} />
                            <AvatarFallback className="text-[10px] font-semibold bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))] text-white">
                              {m.username[0].toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[hsl(var(--nx-panel))] nx-status-online" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm truncate flex items-center gap-1">
                            <span style={{ color: ROLE_COLOR[m.role] || undefined }}>{m.display_name || m.username}</span>
                            {RoleI && <RoleI className="w-3 h-3" style={{ color: ROLE_COLOR[m.role] }} />}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate">@{m.username}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {/* New channel dialog */}
      <Dialog open={openNewChannel} onOpenChange={setOpenNewChannel}>
        <DialogContent className="nx-panel">
          <DialogHeader><DialogTitle className="font-display">Novo canal</DialogTitle></DialogHeader>
          <form onSubmit={createChannel} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input required minLength={1} maxLength={60}
                value={newChannel.name}
                onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value })}
                data-testid="new-channel-name" />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={newChannel.type} onValueChange={(v) => setNewChannel({ ...newChannel, type: v })}>
                <SelectTrigger data-testid="new-channel-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Texto</SelectItem>
                  <SelectItem value="voice">Voz</SelectItem>
                  <SelectItem value="announcement">Anúncios</SelectItem>
                  <SelectItem value="live">Transmissões</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" className="rounded-xl nx-btn-blue border-0 h-11 px-6 font-semibold" data-testid="new-channel-submit">Criar canal</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
